import { domainSchema, nameSchema } from '@hero/schema';
import { z } from 'zod';
import { API } from '..';

/**
 * `EntityDirectory` API — unified network-identity search across the
 * global brand directory (`Profile`) and signed-up Hero `Org`s.
 *
 * Powers the Client-create picker (and any future "pick a known
 * identity" flow) so the CRM admin can converge on a single
 * deduplicated row whether the counterparty is already a Hero Org,
 * a curated Brand Directory entry, or both.
 *
 * Reads are open to any signed-in caller; writes do not exist on
 * this surface (Profile writes are super-admin, `Org` writes happen
 * via signup). See `docs/relations-v2/02-data-model.md` section 7
 * and the relations-v2 plan for the picker contract.
 */

/**
 * One row of the unified directory search result.
 *
 * Dedup contract: when a `Profile` has `linkedOrgId` matching an
 * `Org` hit, the two are collapsed into a single row that carries
 * BOTH ids. The `target` field tells the FE which identity to
 * persist into `Client.linkedParty` (preferred linkage: `profile`
 * when a Profile exists, `org` when only an Org hit was found).
 */
export const entityDirectoryHitSchema = z.object({
  /** display name to render in the picker row. */
  name: nameSchema,
  /**
   * Profile `_id` as a hex string if the row came from / matched a
   * Brand Directory entry. Wire format (string) — consumers convert
   * to {@link ObjectId} via `getObjectId(...)` when constructing
   * `Party.ids[]`.
   */
  profileId: z.string().optional(),
  /** Org `_id` as a hex string if the row came from / matched a signed-up Hero Org. */
  orgId: z.string().optional(),
  /**
   * Canonical `Party.target` the FE should stamp onto
   * `Client.linkedParty` when this row is picked. The picker
   * prefers `profile` (deduplication anchor) over `org` when both
   * ids are present, so downstream `Client.linkedParty` is the
   * stable identity even before signup.
   */
  target: z.enum(['profile', 'org']),
  /** alternate spellings / brand aliases — surfaced for matching context. */
  aliases: z.array(nameSchema).optional(),
  /** primary domain (e.g. `amazon.com`) — surfaced for matching context. */
  domain: domainSchema.optional(),
  /** logo URL for the picker row (Profile / Org logo when present). */
  logoUrl: z.string().url().optional(),
  /** convenience flag — `orgId !== undefined`. */
  isSignedUp: z.boolean(),
});
export type EntityDirectoryHit = z.infer<typeof entityDirectoryHitSchema>;

/**
 * `GET /entity-directory/search?q=...` — unified search across
 * Profile and Org. The server runs both searches in parallel,
 * collapses Profile↔Org pairs via `Profile.linkedOrgId`, and
 * returns a paginated, deduplicated list.
 */
export const entityDirectorySearchAPISchema = z.object({
  /**
   * Free-text query. The server fans this out to:
   * - `Profile`: `name`, `aliases`, `registrationId`, `domain`
   * - `Org`: `name`, `legal.registrationId`, `primaryContact.email`
   */
  q: z.string().trim().min(1),
  /** soft cap on the result count (after dedup). Default 20. */
  limit: z.coerce.number().int().min(1).max(50).optional(),
});
export type EntityDirectorySearchAPI = API<
  z.infer<typeof entityDirectorySearchAPISchema>,
  EntityDirectoryHit[]
>;
